import { type Issue, type Stored } from "@overmap-ai/common"
import { selectIssueTypeById, selectIssueTypeStatusById } from "@overmap-ai/core"
import { memo, useCallback, useState } from "react"
import type { MarkerEvent, MarkerInstance } from "react-map-gl/mapbox"

import { LuDynamicIcon } from "../../components/Icons/LuDynamicIcon"
import type { IconType } from "../../components/Icons/typings"
import { useAppSelector } from "../../store"
import type { ModelAnnotatedEventHandler } from "../../typings"
import { Marker } from "../components"
import { useMarkerEvent } from "../hooks"

interface IssueMarkerProps {
	issue: Stored<Issue>
	active: boolean
	onClick?: ModelAnnotatedEventHandler<MarkerEvent<MouseEvent>, Stored<Issue>>
	onContextMenu?: ModelAnnotatedEventHandler<MarkerEvent<MouseEvent>, Stored<Issue>>
}

export const IssueMarker = memo<IssueMarkerProps>((props) => {
	const { issue, onClick, onContextMenu, active } = props

	const [marker, setMarker] = useState<MarkerInstance | null>(null)

	const issueType = useAppSelector(selectIssueTypeById(issue.issue_type))!
	const status = useAppSelector(selectIssueTypeStatusById(issue.status))!

	const coordinates = issue.geo_marker?.coordinates

	const handleClick = useCallback(
		(e: MarkerEvent<MouseEvent>) => {
			onClick?.({ event: e, model: issue })
		},
		[issue, onClick],
	)

	useMarkerEvent(marker ?? undefined, "click", handleClick)

	const handleContextMenu = useCallback(
		(e: MarkerEvent<MouseEvent>) => {
			onContextMenu?.({ event: e, model: issue })
		},
		[issue, onContextMenu],
	)

	useMarkerEvent(marker ?? undefined, "contextmenu", handleContextMenu)

	if (!coordinates) return null

	return (
		<Marker
			ref={setMarker}
			className="relative h-[35px] w-[20px] cursor-pointer"
			longitude={coordinates[0]}
			latitude={coordinates[1]}
			anchor="bottom"
		>
			<svg
				className="relative block overflow-visible"
				fill="none"
				width="100%"
				height="100%"
				viewBox="0 0 40 70"
				xmlns="http://www.w3.org/2000/svg"
			>
				<path
					fillRule="evenodd"
					clipRule="evenodd"
					fill="var(--base-1)"
					stroke={status?.color}
					strokeWidth={active ? "5" : "3"}
					d="M40 19.8582C40 8.8908 31.0457 0 20 0C8.9543 0 0 8.8908 0 19.8582C0 35.7447 13.3333 59.9054 20 70C26.6667 59.74 40 35.3475 40 19.8582Z"
				/>
			</svg>

			<LuDynamicIcon
				className="absolute left-1/2 top-[30%] block -translate-x-1/2 -translate-y-1/2"
				size="14"
				icon={issueType?.icon as IconType}
				color={issueType?.color}
			/>
		</Marker>
	)
}, areIssueMarkersEqual)
IssueMarker.displayName = "IssueMarker"

function areIssueMarkersEqual(prevProps: IssueMarkerProps, nextProps: IssueMarkerProps) {
	return (
		// List all properties that would influence the look of the marker.
		prevProps.active === nextProps.active &&
		prevProps.issue.issue_type === nextProps.issue.issue_type &&
		prevProps.issue.status === nextProps.issue.status &&
		prevProps.issue.geo_marker?.coordinates[0] === nextProps.issue.geo_marker?.coordinates[0] &&
		prevProps.issue.geo_marker?.coordinates[1] === nextProps.issue.geo_marker?.coordinates[1] &&
		// handlers
		prevProps.onClick === nextProps.onClick &&
		prevProps.onContextMenu === nextProps.onContextMenu
	)
}
